import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { BsInfoCircle } from 'react-icons/bs';
import { toast } from 'react-toastify';
import { Group, GroupService, InvitationRead, InvitationService } from '../../../../api';
import { groupQueryKeys } from '../../../../app/query-keys-factories/groups';
import { invitationQueryKeys } from '../../../../app/query-keys-factories/invitations';
import { userQueryKeys } from '../../../../app/query-keys-factories/user';
import { handleApiError } from '../../../../app/utils/errorhandling';
import { setFormErrorsFromApiErrors } from '../../../../utils/forms';

interface EditInvitationGroupsDialogProps {
  invitation: InvitationRead;
  show: boolean;
  onClose: () => void;
}

export const EditInvitationGroupsDialog = ({
  invitation,
  show,
  onClose,
}: EditInvitationGroupsDialogProps) => {
  const form = useForm();

  const queryGroups = useQuery({
    queryKey: groupQueryKeys.list({}),
    queryFn: async () => {
      let groups: Group[] = [];
      const limit = 100;
      let totalCount: number;
      let offset = 0;

      // fetch all groups considering that data.count is the total number of facilities
      let data = await GroupService.groupList({
        limit,
        offset,
      });
      totalCount = data.count as number;
      groups = groups.concat(data.results || []);
      while (groups.length < totalCount) {
        offset += limit;
        data = await GroupService.groupList({ limit, offset });
        totalCount = data.count as number;
        groups = groups.concat(data.results || []);
      }
      return groups;
    },
    keepPreviousData: true,
  });

  useEffect(() => {
    if (
      !invitation.group_names?.length ||
      (invitation.group_names.length === 1 && invitation.group_names[0] === null)
    ) {
      form.setValue('group_names', false);
    } else {
      form.setValue('group_names', invitation.group_names);
    }
  }, [invitation.group_names]);

  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (data: { group_names: string[] }) => {
      await InvitationService.invitationPartialUpdate({ id: invitation.id, requestBody: data });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(invitationQueryKeys.all());
      queryClient.invalidateQueries(userQueryKeys.all());
    },
  });

  const onSubmit = async (data) => {
    let submitData = data;
    if (typeof data.group_names === 'boolean') {
      submitData = { group_names: [] };
    }
    if (typeof data.group_names === 'string') {
      submitData = { group_names: [data.group_names] };
    }

    try {
      await mutation.mutateAsync(submitData);
      toast.success('User permissions updated successfully.');
      onClose();
    } catch (errors) {
      setFormErrorsFromApiErrors(errors, form.setError);
      handleApiError(errors);
    }
  };

  return (
    <Modal show={show} onHide={onClose} size="lg">
      <Form onSubmit={form.handleSubmit(onSubmit)}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Invited User Permissions</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <p className="mt-1 bg-primary-muted border-1 p-2 text-muted small rounded fw-semibold tw:border-zinc-100">
              <BsInfoCircle className="tw:inline me-2" size="15px" />
              By default, every team member has access to view the marketplace and connected
              providers.
            </p>
            <p className="mb-0">
              Set the appropriate permissions for <b>{invitation.email}</b>
            </p>
          </div>
          {queryGroups.isLoading && <div>Loading...</div>}
          {queryGroups.isError && <div>{JSON.stringify(queryGroups.error)}</div>}
          {queryGroups.isSuccess && (
            <div>
              {queryGroups.data?.map((group) => (
                <Form.Group controlId={`group_names-${group.code}`} key={group.code}>
                  <Form.Check
                    type="switch"
                    value={group.code}
                    label={group.title}
                    {...form.register('group_names')}
                  />
                  <div className="tw:ml-[2.5rem] tw:text-xs tw:text-zinc-500 tw:italic tw:mb-[.6rem]">
                    {group.description}
                  </div>
                </Form.Group>
              ))}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" type="submit">
            Save
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};
